import React, { useEffect, useRef } from 'react';
import { makePieces, stepAll, alphaFor, isDead } from './confettiPhysics';

/**
 * A one-shot confetti burst on a canvas.
 *
 * Hand-rolled rather than pulled from a package: the client carries 19
 * dependencies and this is one celebration on one screen, so a few dozen lines
 * of canvas beats another entry in the bundle and the lockfile.
 *
 * Colours are the Tailwind brand palette (emerald / gold / navy) so the burst
 * reads as part of the product rather than generic party confetti. The physics
 * live in confettiPhysics.js; this file is only rendering.
 */
export default function Confetti({ active = true, pieceCount = 140 }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    // Respect the OS setting — a full-screen burst is exactly the kind of motion
    // people disable it for. Nothing is drawn at all in that case.
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    // Size the backing store to the device pixel ratio, or the pieces render
    // soft on a HiDPI screen.
    const dpr = window.devicePixelRatio || 1;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const pieces = makePieces(width, height, pieceCount);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const alive = stepAll(pieces);

      for (const p of pieces) {
        if (isDead(p)) continue;
        ctx.save();
        ctx.globalAlpha = alphaFor(p);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        if (p.ribbon) {
          ctx.fillRect(-p.size / 2, -p.size / 6, p.size, p.size / 3);
        } else {
          // Scaling height by cos() fakes the piece turning edge-on, which is
          // what makes flat rectangles look like they are tumbling in 3D.
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * Math.abs(Math.cos(p.rotation)));
        }
        ctx.restore();
      }

      if (alive === 0) {
        ctx.clearRect(0, 0, width, height);
        return; // stop the loop; the burst is over
      }
      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [active, pieceCount]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-50"
    />
  );
}
